import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { Toaster } from 'sonner';
import { subscribeUnauthorized } from './api/client';
import { keys } from './api/queries';
import { FullScreenMessage, RequireAuth } from './components/common';
import { TooltipProvider } from './components/ui/overlay';
import './i18n';
import './index.css';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { RouteError } from './pages/RouteError';
import { ScenesPage } from './pages/ScenesPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: true, retry: 1 },
  },
});

// Si una petición responde 401 (sesión caducada), se vuelve a comprobar la sesión: RequireAuth manda al login.
subscribeUnauthorized(() => void queryClient.invalidateQueries({ queryKey: keys.me }));

// El editor (tablero, geometría, paneles) se descarga solo al abrir una escena.
const EditorPage = lazy(() => import('./pages/EditorPage').then((m) => ({ default: m.EditorPage })));

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },
  { path: '/privacidad', element: <PrivacyPage />, errorElement: <RouteError /> },
  { path: '/privacy', element: <PrivacyPage />, errorElement: <RouteError /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <ScenesPage />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
  },
  {
    path: '/escenas/:id',
    element: (
      <RequireAuth>
        <Suspense fallback={<FullScreenMessage>…</FullScreenMessage>}>
          <EditorPage />
        </Suspense>
      </RequireAuth>
    ),
    errorElement: <RouteError />,
  },
  { path: '*', element: <NotFoundPage /> },
]);

const root = document.getElementById('root');
if (!root) throw new Error('Falta #root');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <RouterProvider router={router} />
        <Toaster
          position="bottom-right"
          toastOptions={{
            className: '!bg-surface !text-ink !border-line !shadow-panel !font-sans',
          }}
        />
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
);
